// frontend/src/pages/model/FieldInUseDialog.jsx
// Shown when hiding a field (or ignoring it) would leave saved charts without
// data, or break formulas that read it (13c-3): the server refuses with
// FIELD_IN_USE and lists the charts and the calculations; this asks whether to
// go ahead anyway (the change is then sent again with force: true).
// A Radix dialog, so focus stays inside it, Escape cancels, and focus returns to
// where it was. Focus starts on Cancel, the safe choice.
import React, { useRef } from 'react';
import { Dialog } from 'radix-ui';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { CHARTS_SHOWN, chartHref, confirmLabel, inUseTitle, removalVerb } from './modelLogic';

/**
 * What the dialog says (split out so it can be rendered on its own in tests; it
 * must sit inside a Dialog.Root for its title and description).
 * request: { fieldName, patch, charts: [{ id, title }], count, calculations: [{ key, kind, display_name }] }
 * (count: how many charts use the field; calculations: the formulas that read it, 13c-3).
 */
export function InUseContent({ request, busy = false, onCancel, onConfirm, cancelRef }) {
  const charts = request.charts;
  const calculations = Array.isArray(request.calculations) ? request.calculations : [];
  const shown = charts.slice(0, CHARTS_SHOWN);
  const count = Math.max(request.count || 0, charts.length);
  const more = count - shown.length;
  const what = count && calculations.length ? 'these charts and formulas' : calculations.length ? 'these formulas' : 'these charts';
  return (
    <>
      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-amber-500/10">
        <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden="true" />
      </div>
      <Dialog.Title className="mb-2 text-lg font-semibold tracking-[-0.01em]">{inUseTitle(request.fieldName, count, calculations.length)}</Dialog.Title>
      <Dialog.Description className="mb-3 text-sm text-[var(--ui-text-muted)]">
        {`If you go ahead, ${removalVerb(request.patch)} it takes the field out of ${what}, and they will show an error until someone edits them.`}
      </Dialog.Description>
      <ul className="mb-5 flex max-h-48 flex-col gap-1 overflow-y-auto rounded-xl bg-[var(--ui-inset)] p-3 text-[13px] scrollbar-thin">
        {shown.map((c) => {
          const href = chartHref(c.id);
          const title = c.title || `Chart ${c.id}`;
          return (
            <li key={c.id} className="truncate">
              {href
                ? <a href={href} target="_blank" rel="noopener noreferrer" className="underline-offset-2 hover:underline">{title}</a>
                : title}
            </li>
          );
        })}
        {more > 0 && <li className="text-[var(--ui-text-muted)]">and {more} more</li>}
        {calculations.map((c) => (
          <li key={c.key} className="truncate text-[var(--ui-text-muted)]">{c.kind === 'measure' ? 'Measure' : 'Calculated column'}: {c.display_name}</li>
        ))}
      </ul>
      <div className="flex items-center justify-end gap-3">
        <Button ref={cancelRef} variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="danger" onClick={onConfirm} disabled={busy}>
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
          {confirmLabel(request.patch)}
        </Button>
      </div>
    </>
  );
}

/** request: null (closed), or what InUseContent takes. */
export default function FieldInUseDialog({ request, busy = false, onCancel, onConfirm }) {
  const cancelRef = useRef(null);
  return (
    <Dialog.Root open={Boolean(request)} onOpenChange={(next) => { if (!next && !busy) onCancel(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          onOpenAutoFocus={(e) => { e.preventDefault(); if (cancelRef.current) cancelRef.current.focus(); }}
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-[22px] bg-[var(--ui-panel)] p-6 text-[var(--ui-text)] shadow-[0_24px_60px_rgba(0,0,0,0.6)] ring-1 ring-white/5 focus:outline-none"
        >
          {request && <InUseContent request={request} busy={busy} onCancel={onCancel} onConfirm={onConfirm} cancelRef={cancelRef} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
